import DocumentDO = App.Data.Explorer.DocumentDO;
import {
	formatNumber,
	formatNumberAsPercentageShare,
} from "../Scales/scaleNumberFormatter.js";
import {
	translateLegend,
	translatePropertyFamilyType,
	translatePropertyTitle,
} from "../translationHelper.js";
import { translate as t } from "../i18nBridge.js";
import dayjs from "dayjs";

let tableIndex = 0;

export function resetTableIndex() {
	tableIndex = 0;
}

function createTD(text: string, weight: number): HTMLTableCellElement {
	const element = document.createElement("td");

	element.textContent = text;
	element.style.fontWeight = weight.toString();
	element.style.padding = "3px 6px";
	element.style.minWidth = "100px";
	element.style.width = "100%";
	element.style.whiteSpace = "nowrap";
	if (!(tableIndex % 2)) {
		element.style.backgroundColor = "rgb(var(--color-background))";
	}

	return element;
}

export function pushToTable(table: HTMLTableElement, rowContent: string[]) {
	const row = document.createElement("tr");
	const elements = [];

	for (const [i, text] of rowContent.entries()) {
		const weight = i ? 400 : 500;

		elements.push(createTD(text, weight));
	}
	for (const element of elements) {
		row.appendChild(element);
	}
	table.appendChild(row);
	tableIndex++;
}

function formatNumberOrPercentage(
	derivedDocument: DocumentDO,
	value: number,
	total?: number,
	alwaysShowTotal: boolean = false
) {
	let formattedValue: string;
	if (derivedDocument.is_visualised_as_percentage_share) {
		formattedValue = formatNumberAsPercentageShare(value);
	} else {
		formattedValue = formatNumber(value, derivedDocument.formatForGraph!);
	}

	if (total !== undefined && (alwaysShowTotal || value !== total)) {
		formattedValue += ` (${formatNumberAsPercentageShare(
			(value / total) * 100
		)})`;
	}

	return formattedValue;
}

export function setVertical(
	table: HTMLTableElement,
	derivedDocument: DocumentDO,
	bar: any,
	value: number,
	total?: number
) {
	pushToTable(table, [
		derivedDocument?.legend_dimension === null
			? translateLegend(bar?.legend)
			: t(`properties.${derivedDocument.vertical.name_clean}.title`),
		formatNumberOrPercentage(derivedDocument, value, total),
	]);
}

export function setCombinedValue(
	table: HTMLTableElement,
	derivedDocument: DocumentDO,
	value: number,
	total?: number
) {
	pushToTable(table, [
		"Combined value",
		formatNumberOrPercentage(derivedDocument, value, total, true),
	]);
}

export function setLimitReachedAt(
	table: HTMLTableElement,
	derivedDocument: DocumentDO,
	limitReachedAt: string | undefined
) {
	if (!limitReachedAt) return;

	const granularity = derivedDocument?.horizontal_dimension?.granularity;
	const isBuildup = [
		"buildup",
		"periodreservationdate",
		"transactionbuildup",
	].includes(derivedDocument?.horizontal_dimension?.family_name_clean);

	let format = "";
	if (!isBuildup && (granularity?.includes("min") || granularity === "day")) {
		format = "HH:mm";
	} else if (
		granularity === "week" ||
		granularity === "month" ||
		granularity === "quarter"
	) {
		format = "DD MMM HH:mm";
	} else {
		format = "DD MMM YYYY HH:mm";
	}

	pushToTable(table, [
		dayjs().isBefore(limitReachedAt)
			? "Limit to be reached at"
			: "Limit reached at",
		dayjs(limitReachedAt).format(format),
	]);
}

export function setHorizontal(
	table: HTMLTableElement,
	derivedDocument: DocumentDO,
	titleLine: any
) {
	const granularity = derivedDocument?.horizontal_dimension?.granularity;
	let horizontal = titleLine;
	const horizontalParsed = dayjs(titleLine);

	if (horizontalParsed.isValid()) {
		if (granularity === "day") {
			horizontal = horizontalParsed.format("DD MMM YYYY");
		} else if (granularity === "month") {
			horizontal = horizontalParsed.format("MMM YYYY");
		}
	}

	pushToTable(table, [
		translatePropertyFamilyType(derivedDocument.horizontal_dimension) ||
			"Horizontal",
		horizontal,
	]);
}

export function setOffset(
	table: HTMLTableElement,
	derivedDocument: DocumentDO,
	bar: any
) {
	pushToTable(table, [
		translatePropertyTitle(derivedDocument.offset_dimension) || "Offset",
		bar?.offset,
	]);
}

export function setLegend(
	table: HTMLTableElement,
	derivedDocument: DocumentDO,
	bar: any
) {
	pushToTable(table, [
		translatePropertyTitle(derivedDocument.legend_dimension) || "Legend",
		translateLegend(bar?.legend),
	]);
}
